/* Carousel arrow ink over media (§8do, 2026-10-16).
 *
 * Arrows over an image are white unless the image under the arrow is near white (Rhys:
 * "white unless its a pure white background"), when they turn Blue 20. This reads a
 * 120px-wide copy of the slide in view (a video's poster) under each arrow, mapped
 * through object-fit / object-position and any --overlay-color / --overlay-opacity on
 * the slide; where the arrow overhangs the image (letterboxing, a contained image) the
 * backdrop colour behind the media counts instead. An arrow turns Blue 20 when that
 * area averages relative luminance ≥ 0.85 (about #EDEDED or lighter), each arrow on its
 * own. Arrows stay white if the image can't be read (a cross-origin image without CORS
 * taints the canvas).
 *
 * Usage, from a carousel's inline script, before or after this file has loaded:
 *   (window.lickArrowInk = window.lickArrowInk || []).push((ink) => {
 *     ink.watch({ frame: track, arrows: [prevBtn, nextBtn] });
 *   });
 * frame  - the scroller the slides fill; the slide in view is the one aligned with it.
 * arrows - the .carousel-arrow--on-media buttons over it.
 * slides - optional () => slide elements (default: the frame's children).
 * watch() returns { update() }. Scrolling the frame and resizing it are watched already;
 * call update() after anything else changes the slide in view.
 */
(() => {
  if (window.lickArrowInk && !Array.isArray(window.lickArrowInk)) return; // loaded twice

  const NEAR_WHITE = 0.85;
  const lin = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  const lum = (r, g, b) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);

  // "rgb(…)", "rgba(…)" or "#RRGGBB" → [r, g, b, a], or null
  const parseColour = (str) => {
    str = (str || '').trim();
    let m = /^#?([0-9a-f]{6})$/i.exec(str);
    if (m) { const n = parseInt(m[1], 16); return [n >> 16, (n >> 8) & 255, n & 255, 1]; }
    m = /^rgba?\(([^)]+)\)$/i.exec(str);
    if (!m) return null;
    const p = m[1].split(/[\s,/]+/).filter(Boolean).map(parseFloat);
    return p.length >= 3 ? [p[0], p[1], p[2], p.length > 3 ? p[3] : 1] : null;
  };

  // The colour showing behind an element: the first painted background up the tree
  const backdrop = (el) => {
    for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
      const c = parseColour(getComputedStyle(n).backgroundColor);
      if (c && c[3] > 0) return [c[0] * c[3] + 255 * (1 - c[3]), c[1] * c[3] + 255 * (1 - c[3]), c[2] * c[3] + 255 * (1 - c[3])];
    }
    return [255, 255, 255];
  };

  // One small decoded copy per image, shared by every arrow and carousel
  const pixels = new Map();
  const tinyUrl = (url) => {
    try {
      const u = new URL(url, location.href);
      if (/\/cdn\/shop\/|cdn\.shopify\.com/.test(u.href)) u.searchParams.set('width', '120');
      return u.href;
    } catch (e) { return url; }
  };
  const load = (src) => {
    const key = tinyUrl(src);
    if (!pixels.has(key)) {
      pixels.set(key, new Promise((resolve) => {
        const probe = new Image();
        probe.crossOrigin = 'anonymous';
        probe.decoding = 'async';
        probe.onerror = () => resolve(null);
        probe.onload = () => {
          try {
            const W = probe.naturalWidth, H = probe.naturalHeight;
            if (!W || !H) return resolve(null);
            const canvas = document.createElement('canvas');
            canvas.width = W; canvas.height = H;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(probe, 0, 0);
            resolve({ W, H, data: ctx.getImageData(0, 0, W, H).data });
          } catch (e) { resolve(null); } // tainted canvas
        };
        probe.src = key;
      }));
    }
    return pixels.get(key);
  };

  const position = (v, i) => {
    const part = (v || '').split(/\s+/)[i] || '50%';
    if (part === 'left' || part === 'top') return 0;
    if (part === 'right' || part === 'bottom') return 1;
    return part.endsWith('%') ? parseFloat(part) / 100 : 0.5;
  };

  // Average luminance under one arrow once `slide` fills `frame`, or null if unknown
  const inkUnder = async (arrow, slide, frame) => {
    const media = slide.matches('img, video') ? slide : slide.querySelector('img, video');
    const src = media && (media.tagName === 'IMG' ? (media.currentSrc || media.src) : media.poster);
    const img = media ? (src ? await load(src) : null) : undefined;
    if (media && !img) return null;
    // Every box is read together, after the image has loaded: the track may have moved
    // while it loaded, and the offsets only hold when all are read at the same moment
    const a = arrow.getBoundingClientRect();
    if (!a.width || !a.height) return null; // hidden at this width
    const f = frame.getBoundingClientRect(), s = slide.getBoundingClientRect();
    // the arrow's box in the slide's coordinates, as if the slide were the one in view
    const ax0 = a.left - f.left, ay0 = a.top - f.top, ax1 = a.right - f.left, ay1 = a.bottom - f.top;
    const area = (ax1 - ax0) * (ay1 - ay0);
    const cs = getComputedStyle(media || slide);
    const ov = parseColour(cs.getPropertyValue('--overlay-color'));
    const oa = ov ? (parseFloat(cs.getPropertyValue('--overlay-opacity')) || 0) : 0;
    const shade = (r, g, b) => (ov ? lum(r * (1 - oa) + ov[0] * oa, g * (1 - oa) + ov[1] * oa, b * (1 - oa) + ov[2] * oa) : lum(r, g, b));
    const bg = backdrop(media ? media.parentElement : slide);
    const bgL = shade(bg[0], bg[1], bg[2]);
    if (!media) return bgL;
    const { W, H, data } = img;

    // where the image is drawn inside the media box (object-fit / object-position)
    const m = media.getBoundingClientRect();
    const mx = m.left - s.left, my = m.top - s.top, mw = m.width, mh = m.height;
    if (!mw || !mh) return null;
    const mcs = getComputedStyle(media);
    const fit = mcs.objectFit;
    let kx = mw / W, ky = mh / H; // fill
    if (fit === 'cover') kx = ky = Math.max(mw / W, mh / H);
    else if (fit === 'contain' || fit === 'scale-down') kx = ky = Math.min(mw / W, mh / H);
    const px = position(mcs.objectPosition, 0), py = position(mcs.objectPosition, 1);
    const dx = mx + (mw - W * kx) * px, dy = my + (mh - H * ky) * py;
    // drawn image ∩ media box ∩ arrow, in slide coordinates
    const cx0 = Math.max(ax0, mx, dx), cx1 = Math.min(ax1, mx + mw, dx + W * kx);
    const cy0 = Math.max(ay0, my, dy), cy1 = Math.min(ay1, my + mh, dy + H * ky);
    let imgL = 0, inside = 0;
    if (cx1 > cx0 && cy1 > cy0) {
      const x0 = Math.max(0, Math.floor((cx0 - dx) / kx)), x1 = Math.min(W, Math.max(x0 + 1, Math.ceil((cx1 - dx) / kx)));
      const y0 = Math.max(0, Math.floor((cy0 - dy) / ky)), y1 = Math.min(H, Math.max(y0 + 1, Math.ceil((cy1 - dy) / ky)));
      let sum = 0, n = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const p = (y * W + x) * 4;
          sum += shade(data[p], data[p + 1], data[p + 2]);
          n += 1;
        }
      }
      if (n) { imgL = sum / n; inside = (cx1 - cx0) * (cy1 - cy0); }
    }
    return (imgL * inside + bgL * (area - inside)) / area;
  };

  const watch = ({ frame, arrows, slides } = {}) => {
    arrows = (arrows || []).filter(Boolean);
    if (!frame || !arrows.length) return { update() {} };
    const list = slides || (() => [...frame.children]);
    let shown = null, token = 0, raf = 0;

    const update = async (force) => {
      const all = list();
      if (!all.length) return;
      const f = frame.getBoundingClientRect();
      let best = all[0], gap = Infinity;
      for (const s of all) {
        const r = s.getBoundingClientRect();
        const d = Math.abs(r.left - f.left) + Math.abs(r.top - f.top);
        if (d < gap) { gap = d; best = s; }
      }
      if (best === shown && force !== true) return;
      shown = best;
      const mine = ++token;
      const Ls = await Promise.all(arrows.map((a) => inkUnder(a, best, frame).catch(() => null)));
      if (mine !== token) return; // a newer slide took over while this one loaded
      arrows.forEach((a, i) => {
        if (Ls[i] != null && Ls[i] >= NEAR_WHITE) a.dataset.ink = 'blue';
        else delete a.dataset.ink;
      });
    };

    frame.addEventListener('scroll', () => {
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; update(); });
    }, { passive: true });
    // the arrows sit over a different part of the image at another size
    if ('ResizeObserver' in window) new ResizeObserver(() => update(true)).observe(frame);
    update(true);
    return { update: () => update(true) };
  };

  const api = { watch, NEAR_WHITE };
  const queued = Array.isArray(window.lickArrowInk) ? window.lickArrowInk : [];
  window.lickArrowInk = {
    push(fn) { try { fn(api); } catch (e) { console.error(e); } },
    watch,
  };
  queued.forEach((fn) => window.lickArrowInk.push(fn));
})();
